import { getUnique } from "@/utils/number/unique";
import { AccrualPeriod, formatAccrualPeriod } from "@/entities/models/accrual-period";
import { arrayFrom } from "@/utils/array/array-from";
import { getFilialData } from "./filial-settings";
import { mergeSettings } from "./utils";

const baseFields = {
    personalAccount: "personal_amount",
    bonusAccount: "bonus_amount",
};

const fields = {
    salaryExceptions: "salary_exceptions",
    bonus: "bonus",
    fixDailySalary: "fixed_daily_salary",
    overworkSum: "over",
    underworkSum: "proceeds_min",
    maxBonus: "bonus_max",
    bonusExceptions: "bonus_exceptions",
    penalty: "penalty",
    compens: "compens",
    maxPenalty: "penalty_max",
    penaltyExceptions: "penalty_exceptions",
    workTime_compens : "workTime_compens"
};

// MODELS

function SalaryExceptionModel(data) {
    return {
        id: getUnique(),
        accrualPeriod: AccrualPeriod(data),
        fixDailySalary: data.summa,
    };
}

function formatSalaryException(exception) {
    const { accrualPeriod, fixDailySalary } = exception;

    return {
        ...formatAccrualPeriod(accrualPeriod),
        summa: fixDailySalary,
    };
}

function BonusExceptionModel(data) {
    const { bonus, over: overworkSum, bonus_max: maxBonus } = data;
    return {
        id: getUnique(),
        accrualPeriod: AccrualPeriod(data),

        bonus: bonus || "",
        overworkSum: overworkSum || "",
        maxBonus: maxBonus || "",
    };
}

function formatBonusException(exception) {
    const { accrualPeriod } = exception;
    const formattedData = {
        ...formatAccrualPeriod(accrualPeriod),
    };

    ["bonus", "overworkSum", "maxBonus"].forEach((field) => {
        const fieldName = fields[field];
        const value = exception[field];
        formattedData[fieldName] = value;
    });

    return formattedData;
}

function PenaltyExceptionModel(data) {
    const { penalty, compens: underworkSum, penalty_max: maxPenalty } = data;
    return {
        id: getUnique(),
        accrualPeriod: AccrualPeriod(data),

        penalty: penalty || "",
        underworkSum: underworkSum || "",
        maxPenalty: maxPenalty || "",
    };
}

function formatPenaltyException(exception) {
    const { accrualPeriod } = exception;
    const formattedData = {
        ...formatAccrualPeriod(accrualPeriod),
    };

    ["penalty", "underworkSum", "maxPenalty"].forEach((field) => {
        const fieldName = fields[field];
        const value = exception[field];
        formattedData[fieldName] = value;
    });

    return formattedData;
}

// FORMAT FOR CLIENT

const clientFormatters = {
    salaryExceptions: (value) => arrayFrom(value).map(SalaryExceptionModel),
    bonus: (value) => value || "",
    overworkSum: (value) => value || "",
    maxBonus: (value) => value || "",
    bonusExceptions: (value) => arrayFrom(value).map(BonusExceptionModel),
    penalty: (value) => value || "",
    underworkSum: (value) => value || "",
    maxPenalty: (value) => value || "",
    penaltyExceptions: (value) => arrayFrom(value).map(PenaltyExceptionModel),
};

function formatSalaryFields(settings, emptyFields) {
    const result = {};

    Object.entries(fields).forEach(([field, fieldName]) => {
        if (!emptyFields && !(fieldName in settings)) return;
console.log(emptyFields);
        const value = settings[fieldName];
        if (clientFormatters[field]) {
            result[field] = clientFormatters[field](value);
        } else {
            result[field] = value;
        }
    });

    return result;
}

export function ProductionPositionSalary(positionData, filialId) {
    const {
        fixed_daily_salary: fixDailySalary,
        personal_amount: personalAccount,
        bonus_amount: bonusAccount,
        setting,
    } = positionData;

    const baseSettings = setting;

    let salarySettings = baseSettings;
    let salaryDiff = {};

    if (filialId) {
        const filialSettings = getFilialData(positionData, filialId) || {};

        // Исключения для филиала отдельно

        if (!("bonus_exceptions" in filialSettings)) filialSettings.bonus_exceptions = [];
        if (!("salary_exceptions" in filialSettings)) filialSettings.salary_exceptions = [];
        if (!("penalty_exceptions" in filialSettings)) filialSettings.penalty_exceptions = [];

        const { settings, diff } = mergeSettings(baseSettings, filialSettings);

        salarySettings = settings;
        salaryDiff = diff;
    }

    return {
        value: {
            fixDailySalary,
            personalAccount: !!personalAccount,
            bonusAccount: !!bonusAccount,

            ...formatSalaryFields(salarySettings, true),
        },
        diff: formatSalaryFields(salaryDiff),
    };
}

// FORMAT FOR SERVER

const serverFormatters = {
    personalAccount: (value) => (value ? 1 : 0),
    bonusAccount: (value) => (value ? 1 : 0),
    salaryExceptions: (value) => value.map(formatSalaryException),
    bonusExceptions: (value) => value.map(formatBonusException),
    penaltyExceptions: (value) => value.map(formatPenaltyException),
};

export function formatProductionPositionSalary(positionData) {
    const params = {};

    Object.entries(positionData).forEach(([key, value]) => {
        const fieldName = baseFields[key] || fields[key];
        if (fieldName) {
            if (serverFormatters[key]) {
                params[fieldName] = serverFormatters[key](value);
            } else {
                params[fieldName] = value;
            }
        }
    });

    return params;
}
